import { useMemo, useState } from "react";
import { Ban, Check, Plus, Search, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import type * as type from "@vTypes";

type PairPreference = type.PreferenceData["preference"][number];

interface PairPreferenceEditorProps {
  preferenceData?: type.PreferenceData;
  setPreferenceData: React.Dispatch<
    React.SetStateAction<type.PreferenceData | null>
  >;
  nurseSchema_IdName: { nurse_id: string; name: string }[];
  currentNurseId?: string;
  isEditable: boolean;
}

export function PairPreferenceEditor({
  preferenceData,
  setPreferenceData,
  nurseSchema_IdName,
  currentNurseId,
  isEditable,
}: PairPreferenceEditorProps) {
  const [isAdding, setIsAdding] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedNurseId, setSelectedNurseId] = useState<string | null>(null);
  const pairs = useMemo(
    () => preferenceData?.preference ?? [],
    [preferenceData?.preference]
  );
  const selectedNurse = nurseSchema_IdName.find(
    (nurse) => nurse.nurse_id === selectedNurseId
  );
  const candidates = useMemo(() => {
    const keyword = query.trim().toLocaleLowerCase("ko-KR");
    return nurseSchema_IdName
      .filter((nurse) => nurse.nurse_id !== currentNurseId)
      .filter((nurse) => !pairs.some((pair) => pair.id === nurse.nurse_id))
      .filter(
        (nurse) =>
          !keyword || nurse.name.toLocaleLowerCase("ko-KR").includes(keyword)
      )
      .slice(0, 8);
  }, [currentNurseId, nurseSchema_IdName, pairs, query]);

  const resetAddForm = () => {
    setIsAdding(false);
    setQuery("");
    setSelectedNurseId(null);
  };

  const upsert = (nextPreference: PairPreference) => {
    setPreferenceData((previous) => {
      const current = previous ?? { preference: [], request: "", shift: {} };
      const exists = current.preference.some(
        (item) => item.id === nextPreference.id
      );
      return {
        ...current,
        preference: exists
          ? current.preference.map((item) =>
              item.id === nextPreference.id ? nextPreference : item
            )
          : [...current.preference, nextPreference],
      };
    });
  };

  const remove = (nurseId: string) => {
    setPreferenceData((previous) =>
      previous
        ? {
            ...previous,
            preference: previous.preference.filter(
              (item) => item.id !== nurseId
            ),
          }
        : previous
    );
  };

  return (
    <section aria-label="희망동료 설정" className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-text-main2">희망동료</h2>
          <p className="mt-0.5 text-xs text-text-sub">
            함께 근무하고 싶은 동료 또는 피하고 싶은 동료를 설정하세요.
          </p>
        </div>
        {isEditable && !isAdding ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="border-sig/30 text-sig hover:border-sig/50 hover:bg-sig/5 hover:text-sig"
            onClick={() => setIsAdding(true)}
          >
            <Plus aria-hidden="true" className="size-3.5" />
            동료 추가
          </Button>
        ) : null}
      </div>

      {isAdding ? (
        <div className="rounded-lg bg-light-muted/70 p-2.5">
          {selectedNurse ? (
            <div className="flex items-center justify-between gap-2">
              <span className="min-w-0 truncate text-sm font-medium text-text-main2">
                {selectedNurse.name}
              </span>
              <div className="flex shrink-0 items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-sig hover:bg-sig/10 hover:text-sig"
                  onClick={() => {
                    upsert({ id: selectedNurseId!, weight: 1 });
                    resetAddForm();
                  }}
                >
                  <Check aria-hidden="true" className="size-3.5" />
                  선호
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-amber-800 hover:bg-amber-50 hover:text-amber-800"
                  onClick={() => {
                    upsert({ id: selectedNurseId!, weight: -1 });
                    resetAddForm();
                  }}
                >
                  <Ban aria-hidden="true" className="size-3.5" />
                  비선호
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="동료 선택 취소"
                  className="text-text-sub hover:bg-white"
                  onClick={() => setSelectedNurseId(null)}
                >
                  <X aria-hidden="true" className="size-3.5" />
                </Button>
              </div>
            </div>
          ) : (
            <>
              <label className="flex h-9 items-center gap-2 rounded-md bg-background px-2.5 text-text-sub ring-1 ring-inset ring-border focus-within:ring-sig/50">
                <Search aria-hidden="true" className="size-4 shrink-0" />
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="동료 이름 검색"
                  className="min-w-0 flex-1 bg-transparent text-sm text-text-main2 outline-none placeholder:text-text-sub"
                  aria-label="희망동료 이름 검색"
                />
                <button
                  type="button"
                  aria-label="동료 추가 닫기"
                  className="rounded-sm p-0.5 hover:bg-light-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sig"
                  onClick={resetAddForm}
                >
                  <X aria-hidden="true" className="size-3.5" />
                </button>
              </label>
              <div className="mt-1 max-h-40 overflow-y-auto">
                {candidates.length > 0 ? (
                  candidates.map((nurse) => (
                    <button
                      key={nurse.nurse_id}
                      type="button"
                      className="flex w-full items-center rounded-md px-2.5 py-2 text-left text-sm text-text-main2 hover:bg-background focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sig"
                      onClick={() => setSelectedNurseId(nurse.nurse_id)}
                    >
                      {nurse.name}
                    </button>
                  ))
                ) : (
                  <p className="px-2.5 py-3 text-xs text-text-sub">
                    일치하는 동료가 없습니다.
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      ) : null}

      {pairs.length > 0 ? (
        <ul className="space-y-1.5" aria-label="설정된 희망동료 목록">
          {pairs.map((pair) => {
            const nurse = nurseSchema_IdName.find(
              (item) => item.nurse_id === pair.id
            );
            const isPreferred = pair.weight > 0;
            return (
              <li
                key={pair.id}
                className="flex min-h-9 items-center gap-2 rounded-md bg-light-muted/70 px-2.5 py-1.5"
              >
                <span
                  className={`inline-flex shrink-0 items-center gap-1 text-xs font-semibold ${isPreferred ? "text-sig" : "text-amber-800"}`}
                >
                  {isPreferred ? (
                    <Check aria-hidden="true" className="size-3.5" />
                  ) : (
                    <Ban aria-hidden="true" className="size-3.5" />
                  )}
                  {isPreferred ? "선호" : "비선호"}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-text-main2">
                  {nurse?.name ?? "알 수 없는 동료"}
                </span>
                {isEditable ? (
                  <div className="flex items-center gap-0.5">
                    <button
                      type="button"
                      className="rounded p-1 text-sig hover:bg-sig/10 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sig"
                      aria-label={`${nurse?.name ?? "동료"}를 선호로 변경`}
                      onClick={() => upsert({ ...pair, weight: 1 })}
                    >
                      <Check aria-hidden="true" className="size-3.5" />
                    </button>
                    <button
                      type="button"
                      className="rounded p-1 text-amber-800 hover:bg-amber-50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-600"
                      aria-label={`${nurse?.name ?? "동료"}를 비선호로 변경`}
                      onClick={() => upsert({ ...pair, weight: -1 })}
                    >
                      <Ban aria-hidden="true" className="size-3.5" />
                    </button>
                    <button
                      type="button"
                      className="rounded p-1 text-text-sub hover:bg-background hover:text-text-main2 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sig"
                      aria-label={`${nurse?.name ?? "동료"} 삭제`}
                      onClick={() => remove(pair.id)}
                    >
                      <X aria-hidden="true" className="size-3.5" />
                    </button>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : !isAdding ? (
        <p className="rounded-md bg-light-muted/70 px-2.5 py-3 text-xs text-text-sub">
          등록된 희망동료가 없습니다.
        </p>
      ) : null}
    </section>
  );
}
